import { useContext, useState } from 'react';
import { FormActions } from '../editor/FormActions';
import { QuestionManagementContext } from '@features/question-management/QuestionManagementContainer';
import { MetricOverrideProps } from '../types';
import { MultipleMetricOverride } from './MultipleMetricOverride';
import { SingleMetricOverride } from './SingleMetricOverride';
import { isUtrvConfigCode, MetricOverrideType } from './types';
import { hasDataChanged } from './utils';
import { Loader } from '@g17eco/atoms/loader';
import { MetricOverrideParams } from '@api/initiative-universal-trackers';

interface Props extends Pick<MetricOverrideProps, 'handleCloseModal' | 'formActionsLeftContent'> {
  isLoading: boolean;
  initialForm: MetricOverrideType;
  isMultipleUpdate: boolean;
  handleUpdate: (form: Pick<MetricOverrideParams, 'utrvConfig' | 'aiProcessing'>) => void;
}

export const MetricOverrideForm = (props: Props) => {
  const { isLoading, initialForm, isMultipleUpdate, handleUpdate, handleCloseModal, formActionsLeftContent } = props;
  const [form, setForm] = useState<MetricOverrideType>(initialForm);

  const onChange = <K extends keyof MetricOverrideType>({ code, value }: { code: K; value: MetricOverrideType[K] }) => {
    setForm((prev) => ({ ...prev, [code]: value }));
  };

  const { metricOverrideCode: code } = useContext(QuestionManagementContext);
  const hasAnythingChanged = hasDataChanged(initialForm, form);
  const isAllowedToUpdate = hasAnythingChanged || isMultipleUpdate;

  const onClickUpdate = () => {
    if (!isAllowedToUpdate) {
      return;
    }
    // update one specific property for multiple questions, ex: { utrvConfig: { verificationRequired: 'required' } } or { aiProcessing: 'include' }
    if (code) {
      const data = isUtrvConfigCode(code) ? { utrvConfig: { [code]: form[code] } } : { [code]: form[code] };
      return handleUpdate(data);
    }
    // update all properties for single question
    return handleUpdate({
      utrvConfig: {
        verificationRequired: form.verificationRequired,
        evidenceRequired: form.evidenceRequired,
        noteRequired: form.noteRequired,
        isPrivate: form.isPrivate,
      },
      aiProcessing: form.aiProcessing,
    });
  };

  return (
    <>
      {isLoading ? <Loader /> : null}
      {isMultipleUpdate ? (
        <MultipleMetricOverride hasAnythingChanged={hasAnythingChanged} form={form} handleChange={onChange} />
      ) : (
        <SingleMetricOverride form={form} handleChange={onChange} />
      )}
      <FormActions onCancel={handleCloseModal} onSave={onClickUpdate} isSaveDisabled={isLoading || !isAllowedToUpdate} leftContent={formActionsLeftContent} />
    </>
  );
};
